import {useEffect, useCallback, useState} from 'react';
import {_expand} from 'effects/midi/mappable/lang/operations.js';

const MIN_DRAG = 3;
let counter = 0;

export const useWheel = ({
    ref,
    effect,
    setEffectParameterValues,
    selected,
    isRegionMode,
    setSelected,
    selectedDensity,
    setDensities 
}) => {

    useEffect(() => {
        ref.current.addEventListener(
            "wheel", onWheel, {passive: false});
        return () => {
            ref.current.removeEventListener(
                "wheel", onWheel, {passive: false});
        };
    }, [selected, isRegionMode, setSelected, effect, setDensities]);

    const onWheel = useCallback((e) => {
        let delta = e.wheelDelta;
        if (Math.abs(e.deltaX) >= 0.5) {
            return;
        }
        // if deltaY is a whole number than its not a wheel
        if (e.deltaY - Math.floor(e.deltaY) === 0) {
            return;
        }
        if (Math.abs(delta) >= 50) {
            e.preventDefault();
            e.stopPropagation();
            if (counter++ % 2 !== 0) {
                return false;
            }

            if (isRegionMode) {
                let _selected = selected.flatMap(
                    x => x.isPlaneMacro ? x.parameters : x);
                if (delta < 0) {
                    expandRegions(_selected, -0.1);
                } else {
                    expandRegions(_selected, 0.1);
                }
                return;
            } else if (selectedDensity) {
                let newD = 
                    delta < 0 ? 
                        effect.expandDensitySize(selectedDensity, 0.9) :
                        effect.expandDensitySize(selectedDensity, 1.1);
                setDensities(effect.regions);
            }

            let coords= {};
            let idToParameter = {};
            selected.forEach(
                p => {
                    if (p.isPlaneMacro) {
                        for (let _p of p.parameters) {
                            idToParameter[_p.id] = _p;
                            coords[_p.id] = _p.getCoordinates();
                        }
                    } else {
                        idToParameter[p.id] = p;
                        coords[p.id] = p.getCoordinates();
                    }
                });
            let transformed = {};
            if (delta < 0) {
                // contract by factor
                transformed = _expand(coords, -0.1);
            } else {
                // expand by factor
                transformed = _expand(coords, 0.1);
            }
            if (Object.keys(transformed).length === 0) {
                return false;
            }
            let automation = selected[0] && selected[0].isRecording;
            let _bulkValues = Object.keys(transformed).map(
                    id => idToParameter[id].setCoordinates(
                        transformed[id].x, transformed[id].y, undefined, undefined, automation));
            let bulkValues = _bulkValues.reduce((b,a) => ({... obj(a), ... b}), {});
            setEffectParameterValues(bulkValues);
            return false;
        };
    }, [selected, isRegionMode, setSelected, effect, setDensities]);

    const expandRegions = useCallback((selected, factor) => {
        let values = {};
        for (let p of selected) {
            let {min, max, originalMin, originalMax} = p;
            let expanded = expandMinMax(
                min,
                max,
                originalMin,
                originalMax,
                p.get(true),
                factor);
            p.min = expanded.min;
            p.max = expanded.max;
            if (effect.currentSnapshot) {
                // then update the snapshots min/maxes
                let _p = effect.currentSnapshot.find(
                    _p => _p.parameter.id === p.id);
                if (_p) {
                    _p.min = expanded.min;
                    _p.max = expanded.max;
                }
            }
            values[p.id] = p.get(true);

            if (p.depthParameter) {
                let depth = p.depthParameter;
                let dMin = depth.min;
                let dMax = depth.max;
                let dOriginalMin = depth.originalMin;
                let dOriginalMax = depth.originalMax;

                expanded = expandMinMax(
                    dMin,
                    dMax,
                    dOriginalMin,
                    dOriginalMax,
                    depth.get(true),
                    factor);

                depth.min = expanded.min;
                depth.max = expanded.max;
            }
        }
        setEffectParameterValues(values);
        setSelected([... selected]);
    }, [setSelected, selected]);
};

const expandMinMax = (min, max, originalMin, originalMax, value, factor) => {
    let mid = value;
    let newMin = min + (min - mid) * factor;
    let newMax = max + (max - mid) * factor;

    let _min = Math.max(originalMin, newMin);
    let _max = Math.min(originalMax, newMax);
    if (_min < _max) {
        return {
            min: _min,
            max: _max
        };
    } else {
        return {min, max};
    }
};

const obj = (o) => {
    if (!o) {
        return {};
    }
    let ob = {};
    ob[o.id] = o.value;
    return ob;
};
 
